// src/utils/payeCalculations.js

/**
 * Calculates more realistic PAYE estimates for each vest event, accounting for the
 * progressive nature of HMRC's annual income projections throughout the tax year
 * 
 * @param {Array} vestEvents - Array of RSU vest events sorted by date
 * @param {Object} baseIncome - Base income details (salary, bonus, etc.)
 * @param {boolean} isScottish - Whether the taxpayer is Scottish
 * @param {string} taxYear - The tax year being calculated
 * @returns {Array} - Vest events with enhanced PAYE estimates
 */
export const calculateProgressivePAYE = (vestEvents, baseIncome, isScottish, taxYear) => {
    if (!vestEvents || vestEvents.length === 0) return [];
    
    // Sort events by date to process them chronologically
    const sortedEvents = [...vestEvents].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    const baseSalary = baseIncome.baseSalary || 0;
    const annualBonus = baseIncome.bonus || 0;
    const monthlyBaseSalary = baseSalary / 12;
    
    // Get default tax rates
    const defaultTaxRate = isScottish ? 42 : 40;
    // Higher rate threshold
    const higherRateThreshold = isScottish ? 43662 : 50270;
    // Basic rate
    const basicRate = isScottish ? 21 : 20;
    
    // Running total of income for the year
    let runningIncome = 0;
    // Previous month in calculation (for detecting month changes)
    let previousMonth = null;
    
    return sortedEvents.map(vest => {
      const vestDate = new Date(vest.date);
      const vestMonth = vestDate.getMonth();
      const vestMonthName = vestDate.toLocaleString('default', { month: 'short' });
      const monthsIntoTaxYear = 
        (taxYear.startsWith("2024") ? (vestMonth >= 3 ? vestMonth - 3 : vestMonth + 9) : 
         (vestMonth >= 4 ? vestMonth - 4 : vestMonth + 8));
      
      // If a new month, add the monthly salary to running income
      if (previousMonth !== vestMonth) {
        // Add salary for all months up to and including this one
        // since tax year start (assuming April)
        runningIncome += monthlyBaseSalary * (monthsIntoTaxYear + 1);
        previousMonth = vestMonth;
      }
      
      // Add any previous vest amounts to running income
      // (We're already accounting for RSU employer NIC in the vest events)
      
      // Projected annual income as HMRC might calculate it
      // This uses the number of months into the tax year to project an annual figure
      const monthsRemaining = 12 - (monthsIntoTaxYear + 1);
      
      // Calculate estimated annual income based on current trends
      // HMRC typically projects by multiplying current income by 12/months_so_far
      const projectionFactor = 12 / (monthsIntoTaxYear + 1);
      let projectedAnnualIncome = runningIncome * projectionFactor;
      
      // If we're 9+ months into the tax year, HMRC would have a good idea of actual annual income
      if (monthsIntoTaxYear >= 9) {
        projectedAnnualIncome = runningIncome + (monthlyBaseSalary * monthsRemaining);
      }
      
      // If bonus is typically paid in a specific month and hasn't been included yet
      // This is simplified - in reality, HMRC would factor in previous year's bonus pattern
      // We're assuming typical bonus payout in month 3 (usually June, or month 3 of tax year)
      const bonusMonth = 3; // June
      if (monthsIntoTaxYear < bonusMonth) {
        // Bonus is expected later in the year
        projectedAnnualIncome += annualBonus;
      } else if (monthsIntoTaxYear === bonusMonth) {
        // Bonus likely just paid, already in running income
        // But for projection, we don't multiply it
        projectedAnnualIncome = (runningIncome - annualBonus) * projectionFactor + annualBonus;
      }
      
      // Calculate RSU amount subject to income tax
      const employerNicRate = vest.employer_nic_rate || 6.9;
      const rsuSubjectToTax = vest.amount * (1 - employerNicRate/100);
      
      // Add this RSU event to running income
      runningIncome += rsuSubjectToTax;
      
      // Determine applicable tax rate based on projected income
      let effectiveRate = basicRate / 100;
      if (projectedAnnualIncome > higherRateThreshold) {
        effectiveRate = defaultTaxRate / 100;
      }
      
      // Determine withholding amount (what actually happened)
      const withholdingRate = vest.withholding_rate || 50;
      const withholdingAmount = vest.amount * (withholdingRate/100);
      
      // Calculate what PAYE would have actually shown on the payslip
      const payeAmount = rsuSubjectToTax * effectiveRate;
      
      // Difference between PAYE and withholding
      const difference = payeAmount - withholdingAmount;
      
      return {
        ...vest,
        running_income: runningIncome,
        projected_annual_income: projectedAnnualIncome,
        months_into_tax_year: monthsIntoTaxYear + 1,
        month_name: vestMonthName,
        estimated_paye: {
          subject_to_tax: rsuSubjectToTax,
          withholding_amount: withholdingAmount,
          paye_amount: payeAmount,
          difference: difference,
          effective_rate: effectiveRate * 100,
          projected_annual_income: projectedAnnualIncome
        }
      };
    });
  };
  
  /**
   * Formats a number as GBP currency
   */
  export const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };